import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useT } from '../i18n';
import { Brand } from '../app/Brand';
import { Button } from '../ui';
import { useApi } from '../api/ApiProvider';
import { useAuthStore } from './store';
import s from './SelectOrgPage.module.css';

/** Cible du lien de l'e-mail d'invitation : accepte l'invitation, puis bascule sur l'organisation rejointe. */
export function AcceptInvitationPage() {
  const { t } = useT();
  const api = useApi();
  const { orgId = '', invitationId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setUser = useAuthStore((x) => x.setUser);
  const setOrgId = useAuthStore((x) => x.setOrgId);
  const [state, setState] = useState<'pending' | 'done' | 'error'>('pending');
  const [attempt, setAttempt] = useState(0);
  const started = useRef(-1);

  useEffect(() => {
    // Un seul envoi par tentative (le mode strict de React monte deux fois les effets).
    if (started.current === attempt) return;
    started.current = attempt;
    setState('pending');
    api
      .acceptInvitation(orgId, invitationId)
      .then(() => api.me())
      .then((user) => {
        qc.removeQueries({ queryKey: ['org'] });
        setUser(user);
        setOrgId(orgId);
        setState('done');
        navigate('/', { replace: true });
      })
      .catch(() => setState('error'));
  }, [api, attempt, invitationId, navigate, orgId, qc, setOrgId, setUser]);

  return (
    <div className={s.page}>
      <header className={s.top}>
        <Brand />
      </header>
      <main className={s.main}>
        {state === 'error' ? (
          <>
            <p role="alert">{t('invitation.accept.error')}</p>
            <Button onClick={() => setAttempt((n) => n + 1)}>{t('invitation.accept.retry')}</Button>
          </>
        ) : (
          <p role="status">{t(state === 'done' ? 'invitation.accept.done' : 'invitation.accept.pending')}</p>
        )}
      </main>
    </div>
  );
}
